import { useEffect, useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Input, Select } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { Gift, GiftCategory } from "../api/types";
import { UploadButton } from "../components/ui/UploadButton";

// Real gift catalog: icon (PNG, shown in the app's gift picker) and an
// optional .svga animation URL, played on the sender's and recipient's
// screens when the gift is sent. coins_cost here is the ONLY source of
// truth for what a gift actually costs — the Worker looks this up
// server-side on every send, never trusting a client-supplied amount.
//
// Round 37 (migration 0081) — four more real, consumed flags:
//   server_broadcast / all_room_broadcast: decide REAL server-side
//     broadcast behavior in handleSendGift (coins.js) — never anything
//     the client decides, per this project's established rule.
//     Round 45 item 1 fix: server_broadcast is now a FORCE-OVERRIDE only
//     ("broadcast this gift regardless of value") — the Global Gift
//     Banner's real, primary trigger is the admin-configured value
//     threshold on the Gift Broadcast Levels page, checked automatically
//     on every send. Previously server_broadcast was the ONLY trigger,
//     defaulted off, and was never turned on for any gift — which is why
//     the banner never appeared for any real gift send before this fix.
//   has_music / has_animation: read by the Android app to gate real
//     playback (VoiceRoomActivity.playGiftAnimation for has_animation;
//     has_music is carried through the model for a future sound pipeline
//     — no gift plays sound today, see this round's report).
//
// Delete: switched from a hard DELETE to the same "disable instead of
// destroy" convention MedalsPage/Coin Products use, because gifts(id) is
// referenced by gift_receipts with `on delete cascade` (migration
// 0052) — a hard delete here would silently wipe every real Gift Wall /
// ranking record for that gift, not just remove it from the picker.
// Deactivating (is_active=false) removes it from the app's gift picker
// immediately (gifts' own RLS policy only exposes is_active=true rows)
// while keeping gift_receipts history intact.
export function GiftCatalogPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<Gift>("/admin/gift-catalog");
  const [categories, setCategories] = useState<GiftCategory[]>([]);
  const [editing, setEditing] = useState<Partial<Gift> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    apiRequest<GiftCategory[]>("/admin/gift-categories")
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  function categoryName(id: number) {
    return categories.find((c) => c.id === id)?.name ?? `#${id}`;
  }

  async function saveGift() {
    if (!editing?.name || !editing.category_id || !editing.coins_cost || !editing.icon_url) {
      showToast("Name, category, coins cost, and icon URL are all required", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      const body = {
        name: editing.name,
        category_id: editing.category_id,
        coins_cost: editing.coins_cost,
        icon_url: editing.icon_url,
        animation_url: editing.animation_url || null,
        sort_order: editing.sort_order ?? 0,
        is_active: editing.is_active ?? true,
        server_broadcast: editing.server_broadcast ?? false,
        all_room_broadcast: editing.all_room_broadcast ?? false,
        has_music: editing.has_music ?? true,
        has_animation: editing.has_animation ?? true,
        is_proposal: editing.is_proposal ?? false,
      };
      if (editing.id) {
        await apiRequest(`/admin/gift-catalog/${editing.id}`, { method: "PATCH", body });
      } else {
        await apiRequest("/admin/gift-catalog", { method: "POST", body });
      }
      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save gift", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function toggleActive(row: Gift) {
    try {
      await apiRequest(`/admin/gift-catalog/${row.id}`, { method: "PATCH", body: { is_active: !row.is_active } });
      showToast(row.is_active ? "Deactivated" : "Activated", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update gift", "error");
    }
  }

  function yesNoSelect(
    label: string,
    field: "server_broadcast" | "all_room_broadcast" | "has_music" | "has_animation" | "is_proposal",
    hint?: string
  ) {
    return (
      <Select
        label={label}
        hint={hint}
        value={(editing?.[field] ?? (field === "has_music" || field === "has_animation")) ? "yes" : "no"}
        onChange={(e) => setEditing((p) => ({ ...p, [field]: e.target.value === "yes" }))}
      >
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </Select>
    );
  }

  const columns: Column<Gift>[] = [
    {
      header: "Gift",
      render: (row) => (
        <div className="flex items-center gap-2">
          <img src={row.icon_url} className="h-10 w-10 rounded-lg object-cover" alt="" />
          <div>
            <p className="font-medium text-ink">{row.name}</p>
            <p className="text-xs text-ink-faint">{categoryName(row.category_id)}</p>
          </div>
        </div>
      ),
    },
    { header: "Cost", render: (row) => `${row.coins_cost} coins` },
    { header: "Animation", render: (row) => (row.animation_url ? <Badge tone="gold">.svga</Badge> : <span className="text-xs text-ink-faint">icon only</span>) },
    {
      header: "Broadcast",
      render: (row) => (
        <div className="flex flex-col gap-1">
          {row.server_broadcast && <Badge tone="gold">Server-wide</Badge>}
          {row.all_room_broadcast && <Badge tone="gold">All rooms</Badge>}
          {!row.server_broadcast && !row.all_room_broadcast && <span className="text-xs text-ink-faint">Own room only</span>}
        </div>
      ),
    },
    {
      header: "Music / Anim",
      render: (row) => (
        <span className="text-xs text-ink-faint">
          {row.has_music ? "Music" : "No music"} · {row.has_animation ? "Anim" : "No anim"}
        </span>
      ),
    },
    { header: "Status", render: (row) => (row.is_active ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>) },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditing(row)}>
            Edit
          </Button>
          <Button size="sm" variant={row.is_active ? "danger" : "secondary"} onClick={() => toggleActive(row)}>
            {row.is_active ? "Deactivate" : "Activate"}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="Gift Catalog">
      <p className="mb-4 max-w-2xl text-xs text-ink-faint">
        coins_cost here is the real, server-verified price — the app never trusts a client-sent amount when a gift is
        sent. Server Broadcast / All-Room Broadcast are enforced server-side on every send, never by the client.
        There's no hard delete here on purpose — gift_receipts (Gift Wall, rankings) references gifts forever, so
        deactivate instead.
      </p>
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setEditing({})} disabled={categories.length === 0}>
          + Add Gift
        </Button>
      </div>
      {categories.length === 0 && !isLoading && (
        <p className="mb-4 text-xs text-ink-faint">Create a gift category first — a gift must belong to one.</p>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No gifts yet"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit Gift" : "Add Gift"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={saveGift} isLoading={isSubmitting}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          <section>
            <h3 className="mb-3 text-sm font-semibold text-ink">Identity</h3>
            <div className="space-y-3">
              <Input label="Name" value={editing?.name ?? ""} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} />
              <div className="grid grid-cols-2 gap-3">
                <Select
                  label="Category"
                  value={editing?.category_id ?? ""}
                  onChange={(e) => setEditing((p) => ({ ...p, category_id: Number(e.target.value) }))}
                >
                  <option value="" disabled>
                    Choose a category
                  </option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
                <Input
                  label="Coins cost"
                  type="number"
                  value={editing?.coins_cost ?? ""}
                  onChange={(e) => setEditing((p) => ({ ...p, coins_cost: Number(e.target.value) }))}
                />
              </div>
            </div>
          </section>

          <section className="border-t border-surface-border pt-4">
            <h3 className="mb-3 text-sm font-semibold text-ink">Assets</h3>
            <div className="space-y-3">
              <Input label="Icon URL (PNG)" value={editing?.icon_url ?? ""} onChange={(e) => setEditing((p) => ({ ...p, icon_url: e.target.value }))} />
              <UploadButton kind="gift_icon" onUploaded={(u) => setEditing((p) => ({ ...p, icon_url: u }))} />
              <Input
                label="Animation URL (.svga, optional)"
                value={editing?.animation_url ?? ""}
                onChange={(e) => setEditing((p) => ({ ...p, animation_url: e.target.value }))}
                hint="Leave blank for an icon-only gift with no play animation"
              />
            </div>
          </section>

          <section className="border-t border-surface-border pt-4">
            <h3 className="mb-3 text-sm font-semibold text-ink">Broadcast Behavior</h3>
            <div className="grid grid-cols-2 gap-3">
              {yesNoSelect("Server Broadcast", "server_broadcast", "Forces the platform-wide Global Gift Banner for this gift on every send, regardless of value. Leave this off for ordinary gifts — the banner already fires automatically once a send's total value reaches a threshold configured on the Gift Broadcast Levels page; this toggle is only for forcing it below that threshold.")}
              {yesNoSelect("All-Room Broadcast", "all_room_broadcast", "Broadcasts into every live room's chat, not just the sender's own")}
            </div>
          </section>

          <section className="border-t border-surface-border pt-4">
            <h3 className="mb-3 text-sm font-semibold text-ink">Playback</h3>
            <div className="grid grid-cols-2 gap-3">
              {yesNoSelect("Gift Has Music", "has_music")}
              {yesNoSelect("Gift Has Animation", "has_animation", "No means the app shows the static icon even with an animation URL set")}
            </div>
          </section>

          <section className="border-t border-surface-border pt-4">
            <h3 className="mb-3 text-sm font-semibold text-ink">Other</h3>
            <div className="grid grid-cols-2 gap-3">
              {yesNoSelect("Gift Proposal (CP)", "is_proposal", "ON: when a user sends this gift to another user, the two become CP couple (shown on both profiles). A user can only be in one CP at a time.")}
              <Select
                label="Status"
                value={(editing?.is_active ?? true) ? "yes" : "no"}
                onChange={(e) => setEditing((p) => ({ ...p, is_active: e.target.value === "yes" }))}
              >
                <option value="yes">Active</option>
                <option value="no">Inactive</option>
              </Select>
            </div>
            <div className="mt-3">
              <Input
                label="Sort order"
                type="number"
                value={editing?.sort_order ?? 0}
                onChange={(e) => setEditing((p) => ({ ...p, sort_order: Number(e.target.value) }))}
              />
            </div>
          </section>
        </div>
      </Modal>
    </AppShell>
  );
}
